import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { messageFromError } from '@/lib/api';
import {
  channelConnectionsQuery,
  useDeleteChannelConnection,
  useSetConnectionActive,
  useTestConnection,
  PROVIDER_LABELS,
} from '@/lib/channels';
import type { ChannelConnection } from '@/lib/channels';
import { usePageHeader } from '@/components/PageHeader';
import { useToast } from '@/components/ToastProvider';
import { useConfirm } from '@/components/DialogProvider';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

export const Route = createFileRoute('/_app/settings/channels')({
  component: ChannelAccountsPage,
});

export function ChannelAccountsPage() {
  usePageHeader({ subtitle: 'Channel accounts' });

  const { data: connections = [], isLoading } = useQuery(channelConnectionsQuery);
  const { toast } = useToast();
  const askConfirm = useConfirm();
  const testConn = useTestConnection();
  const deleteConn = useDeleteChannelConnection();
  const setActive = useSetConnectionActive();

  async function handleToggleActive(conn: ChannelConnection) {
    const target = conn.status !== 'active';
    try {
      await setActive.mutateAsync({ id: conn.id, active: target });
      toast({
        kind: 'success',
        title: target ? 'Channel activated' : 'Channel deactivated',
      });
    } catch (e) {
      toast({
        kind: 'error',
        title: target ? 'Activate failed' : 'Deactivate failed',
        message: messageFromError(e),
      });
    }
  }

  async function handleTest(conn: ChannelConnection) {
    try {
      await testConn.mutateAsync(conn.id);
      toast({ kind: 'success', title: 'Test passed', message: `${conn.label} is reachable` });
    } catch (e) {
      toast({
        kind: 'error',
        title: 'Test failed',
        message: messageFromError(e),
      });
    }
  }

  async function handleDelete(conn: ChannelConnection) {
    const ok = await askConfirm({
      title: `Delete "${conn.label}"?`,
      body: 'This will remove the account connection. Apartments mapped to it will lose their channel link.',
      destructive: true,
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    try {
      await deleteConn.mutateAsync(conn.id);
      toast({ kind: 'success', title: 'Connection deleted' });
    } catch (e) {
      toast({
        kind: 'error',
        title: 'Delete failed',
        message: messageFromError(e),
      });
    }
  }

  if (isLoading) {
    return (
      <div className="py-8 text-center text-sm text-muted">Loading…</div>
    );
  }

  if (connections.length === 0) {
    return (
      <div className="rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card px-6 py-12 text-center">
        <p className="text-[15px] font-semibold text-[var(--text-heading)]">No channel accounts</p>
        <p className="mt-2 text-sm text-muted">
          Connect accounts from the Apartments → Channels tab for each property.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {connections.map((conn) => (
        <div
          key={conn.id}
          className="flex items-center gap-3 rounded-[var(--radius-control)] border border-[var(--border-hairline)] bg-card px-4 py-3"
        >
          <div className="flex-1 min-w-0">
            <p className="truncate text-sm font-medium text-foreground">{conn.label}</p>
            <p className="text-xs text-muted">
              {conn.provider in PROVIDER_LABELS
                ? PROVIDER_LABELS[conn.provider as keyof typeof PROVIDER_LABELS]
                : conn.provider}
            </p>
          </div>
          <ConnectionStatusBadge status={conn.status} />
          <div className="flex shrink-0 gap-2">
            {conn.channex_channel_id && (
              // Only a mapped connection has a Channex channel to (de)activate.
              // Gating on the channel (not just status) hides Activate on an
              // inactive-but-unmapped connection, which would 500 ("map first").
              <Button
                type="button"
                variant={conn.status === 'active' ? 'secondary' : 'primary'}
                size="sm"
                loading={setActive.isPending && setActive.variables?.id === conn.id}
                onClick={() => handleToggleActive(conn)}
              >
                {conn.status === 'active' ? 'Deactivate' : 'Activate'}
              </Button>
            )}
            <Button
              type="button"
              variant="secondary"
              size="sm"
              loading={testConn.isPending && testConn.variables === conn.id}
              onClick={() => handleTest(conn)}
            >
              Test
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              loading={deleteConn.isPending && deleteConn.variables === conn.id}
              onClick={() => handleDelete(conn)}
            >
              Delete
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function ConnectionStatusBadge({ status }: { status: ChannelConnection['status'] }) {
  if (status === 'active') return <Badge variant="success" size="sm" dot>Active</Badge>;
  if (status === 'error') return <Badge variant="danger" size="sm">Error</Badge>;
  if (status === 'pending_auth') return <Badge variant="warning" size="sm">Pending auth</Badge>;
  return <Badge variant="neutral" size="sm">Inactive</Badge>;
}
